import { Edit, Logout, Settings } from '@mui/icons-material';
import {
    Box,
    Chip,
    Divider,
    IconButton,
    ListItemIcon,
    Menu,
    MenuItem,
    Tooltip,
    Typography,
    useTheme,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
import { ELinks } from '@sharedInterfaces/globalEnums';
import { generateFullName } from '@sharedInterfaces/globalHelper';
import { getLanguageTexts } from '@sharedInterfaces/Language/languageHelper';
import type { AppState } from '@store/store';
import { Ellipsis } from 'lucide-react';
import React from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { logout } from '../../Buttons/UserButton/UserButton';
import { EmployeePicture } from '../EmployeePicture/EmployeePicture';

interface ProfileAvatarProps {
    isCollapsed?: boolean;
}

/**
 * ProfileAvatar component for displaying user information in the navigation sidebar.
 *
 * @param {ProfileAvatarProps} props - The props for the ProfileAvatar component.
 * @returns {JSX.Element} - The rendered ProfileAvatar component.
 */
export function ProfileAvatar({ isCollapsed = false }: ProfileAvatarProps) {
    const theme = useTheme();
    const navigate = useNavigate();
    const lang = useSelector((state: AppState) => state.employee.language);
    const langStrings = getLanguageTexts(lang).navigation;

    const employee = useSelector((state: AppState) => state.employee);
    const permissions = useSelector((state: AppState) => state.permissions);
    const offline = useSelector((state: AppState) => state.client.offline);
    const OUs = useSelector((state: AppState) => state.OUs);
    const personas = useSelector((state: AppState) => state.personas);
    const featureFlags = useSelector((state: AppState) => state.company.featureFlags);

    // Finde die OrgUnit des Mitarbeiters, genau wie in GeneralInfo.tsx
    const OU = employee && OUs ? OUs.find((o) => o.employees.includes(employee.id)) : undefined;

    const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);

    const handleMoreClick = (event: React.MouseEvent<HTMLElement>) => {
        setAnchorEl(event.currentTarget);
    };

    const handleClose = () => {
        setAnchorEl(null);
    };

    const handleAvatarClick = () => {
        navigate(`/${ELinks.PROFILE}`);
    };

    const handleOrgUnitClick = () => {
        if (OU && featureFlags.organizations) {
            navigate(`/${ELinks.PEOPLE_ORGUNITS}/${OU.id}`);
        }
    };

    const handleEditProfile = () => {
        navigate(`/${ELinks.PROFILE_EDIT}`);
        handleClose();
    };

    const handleSettings = () => {
        navigate(`/${ELinks.SETTINGS}`);
        handleClose();
    };

    const handleLogout = () => {
        logout();
        handleClose();
    };

    return (
        <Box
            sx={{
                height: isCollapsed ? 100 : 100,
                p: isCollapsed ? 1 : 2,
                display: 'flex',
                flexDirection: isCollapsed ? 'column' : 'row',
                alignItems: 'center',
                gap: isCollapsed ? 1 : 1.5,
                backgroundColor: alpha(theme.palette.primary.main, 0.04),
                borderBottom: `1px solid ${theme.palette.divider}`,
                position: 'relative',
                width: '100%',
                boxSizing: 'border-box',
                overflow: 'hidden',
            }}
        >
            {/* User Avatar */}
            <Tooltip title={employee ? generateFullName(employee.firstName, employee.lastName) : ''}>
                <Box
                    onClick={handleAvatarClick}
                    sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        justifyContent: 'center',
                        ml: isCollapsed ? 0 : 1,
                    }}
                >
                    <EmployeePicture avatarSize={isCollapsed ? 40 : 48} employee={employee} />
                </Box>
            </Tooltip>

            {/* User Info - Only show when not collapsed */}
            {!isCollapsed && (
                <Box
                    sx={{
                        flex: 1,
                        minWidth: 0,
                        overflow: 'hidden',
                        maxWidth: 'calc(100% - 80px)',
                    }}
                >
                    <Typography
                        variant="subtitle2"
                        sx={{
                            fontWeight: 600,
                            color: theme.palette.text.primary,
                            lineHeight: 1.2,
                            mb: 0.5,
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                        }}
                    >
                        {employee ? generateFullName(employee.firstName, employee.lastName) : ''}
                    </Typography>
                    <Typography
                        variant="caption"
                        onClick={handleOrgUnitClick}
                        sx={{
                            color: theme.palette.text.secondary,
                            lineHeight: 1,
                            display: 'block',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            whiteSpace: 'nowrap',
                            maxWidth: '100%',
                            cursor: OU && featureFlags.organizations ? 'pointer' : 'default',
                            '&:hover': {
                                textDecoration: OU && featureFlags.organizations ? 'underline' : 'none',
                            },
                        }}
                    >
                        {OU ? OU.title : '-'}
                    </Typography>

                    {/* Personas-Anzeige */}
                    <Box sx={{ display: 'flex', gap: 0.25, mt: 1, flexWrap: 'wrap' }}>
                        {personas.map((persona) => (
                            <Chip
                                key={persona}
                                label={persona}
                                size="small"
                                variant="outlined"
                                sx={{
                                    borderColor:
                                        persona === 'Admin'
                                            ? theme.palette.error.main
                                            : persona === 'FK'
                                              ? theme.palette.warning.main
                                              : theme.palette.success.main,
                                    color:
                                        persona === 'Admin'
                                            ? theme.palette.error.main
                                            : persona === 'FK'
                                              ? theme.palette.warning.main
                                              : theme.palette.success.main,
                                    fontSize: '0.6rem',
                                    height: 20,
                                    borderRadius: '8px',
                                    '& .MuiChip-label': {
                                        px: 0.5,
                                        fontSize: '0.6rem',
                                        fontWeight: 200,
                                    },
                                }}
                            />
                        ))}
                    </Box>
                </Box>
            )}

            {/* More Options Button */}
            <IconButton
                size="small"
                onClick={handleMoreClick}
                sx={{
                    color: theme.palette.text.secondary,
                    backgroundColor: alpha(theme.palette.action.hover, 0.0),
                    flexShrink: 0,
                    width: 32,
                    height: 32,
                    '&:hover': {
                        backgroundColor: alpha(theme.palette.action.hover, 0.2),
                    },
                    ...(isCollapsed && {
                        mt: 1,
                    }),
                }}
            >
                <Ellipsis size={16} />
            </IconButton>

            {/* More Options Menu */}
            <Menu
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                PaperProps={{
                    elevation: 0,
                    sx: {
                        overflow: 'visible',
                        filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
                        mt: 1.5,
                        '& .MuiAvatar-root': {
                            width: 32,
                            height: 32,
                            ml: -0.5,
                            mr: 1,
                        },
                        '&:before': {
                            content: '""',
                            display: 'block',
                            position: 'absolute',
                            top: 0,
                            right: 10,
                            width: 10,
                            height: 10,
                            bgcolor: 'background.paper',
                            transform: 'translateY(-50%) rotate(45deg)',
                            zIndex: 0,
                        },
                        '& .MuiListItemIcon-root': {
                            color: 'var(--color-primary)',
                            minWidth: 36,
                        },
                    },
                }}
                transformOrigin={{ horizontal: 'right', vertical: 'top' }}
                anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
            >
                <MenuItem onClick={handleEditProfile}>
                    <ListItemIcon>
                        <Edit fontSize="small" />
                    </ListItemIcon>
                    {langStrings.editProfile}
                </MenuItem>
                {permissions.admin && (
                    <MenuItem onClick={handleSettings} disabled={offline}>
                        <ListItemIcon>
                            <Settings fontSize="small" />
                        </ListItemIcon>
                        {langStrings.settings}
                    </MenuItem>
                )}
                <Divider />
                <MenuItem onClick={handleLogout} disabled={offline}>
                    <ListItemIcon>
                        <Logout fontSize="small" />
                    </ListItemIcon>
                    {langStrings.logout}
                </MenuItem>
            </Menu>
        </Box>
    );
}

export default ProfileAvatar;
